import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Divider,
  ListItemText,
  Paper,
  Autocomplete,
  TextField,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ViewKanban, ViewList, Add, Lock, Clear } from '@mui/icons-material';
import { BoardWithCreator, User, WorkItemFilters } from 'api/todo/types';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';

const MAX_CASE_TITLE_LENGTH = 30;

interface TodoHeaderProps {
  boards: BoardWithCreator[];
  currentBoardId: number | null;
  view: 'kanban' | 'list';
  isOwner: boolean;
  users: User[];
  filters: WorkItemFilters;
  onFiltersChange: (filters: WorkItemFilters) => void;
  availableCases: { case_id: number; case_title: string }[];
  onSelectBoard: (boardId: number) => void;
  onViewChange: (view: 'kanban' | 'list') => void;
  onNewTask: () => void;
  onCreateBoard: () => void;
  onManageBoard: () => void;
}

export const TodoHeader: React.FC<TodoHeaderProps> = ({
  boards,
  currentBoardId,
  view,
  isOwner,
  users,
  filters,
  onFiltersChange,
  availableCases,
  onSelectBoard,
  onViewChange,
  onNewTask,
  onCreateBoard,
  onManageBoard,
}) => {
  const [boardSelectOpen, setBoardSelectOpen] = useState(false);
  const { userInfo } = useSelector(selectUserInfo);

  const currentUserId = parseInt(userInfo.userId || '0');

  const selectedAssignees = users.filter((u) => (filters.assigneeIds || []).includes(u.user_id));
  const selectedCases = availableCases.filter((c) => (filters.caseIds || []).includes(c.case_id));
  const hasActiveFilters = (filters.assigneeIds && filters.assigneeIds.length > 0) || (filters.caseIds && filters.caseIds.length > 0);

  const truncateTitle = (title: string) => {
    if (title.length <= MAX_CASE_TITLE_LENGTH) return title;
    return title.substring(0, MAX_CASE_TITLE_LENGTH) + '…';
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1, sm: 1 },
        p: { xs: 1.5, sm: 2 },
        m: { xs: 1, sm: 2 },
        mb: 0,
      }}
    >
      <Typography variant="h4" sx={{mb: 2}}>
        To-Do Boards
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 },
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
      {/* Board Selector */}
      <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 250 }, width: { xs: '100%', sm: 'auto' } }}>
        <InputLabel>Board</InputLabel>
        <Select
          value={currentBoardId || ''}
          label="Board"
          open={boardSelectOpen}
          onClose={() => setBoardSelectOpen(false)}
          onOpen={() => setBoardSelectOpen(true)}
          onChange={(e) => {
            const boardId = e.target.value as number;
            if (boardId && boardId !== -1) {
              onSelectBoard(boardId);
            } else if (boardId === -1) {
              onCreateBoard();
              setBoardSelectOpen(false);
            }
          }}
        >
          {Array.isArray(boards) && boards.map((board) => (
            <MenuItem key={board.board_id} value={board.board_id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                {board.is_private && <Lock sx={{ fontSize: 16 }} />}
                <ListItemText
                  primary={board.name}
                  secondary={
                    board.creator_user_id === currentUserId
                      ? 'Created by you'
                      : `Created by ${board.creator_name}`
                  }
                />
              </Box>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem value={-1}>
            <Add sx={{ mr: 1, fontSize: 20 }} />
            Create New Board
          </MenuItem>
        </Select>
      </FormControl>

      {/* View Toggle and Action Buttons Row */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          width: { xs: '100%', sm: 'auto' },
          flexWrap: 'wrap',
        }}
      >
        {/* View Toggle */}
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, newView) => newView && onViewChange(newView)}
          size="small"
          sx={{ flex: { xs: 1, sm: 'none' } }}
        >
          <ToggleButton value="kanban" sx={{ px: { xs: 1, sm: 2 } }}>
            <ViewKanban sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: 18, sm: 20 } }} />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Kanban</Box>
          </ToggleButton>
          <ToggleButton value="list" sx={{ px: { xs: 1, sm: 2 } }}>
            <ViewList sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: 18, sm: 20 } }} />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>List</Box>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Manage Board Button */}
        {isOwner && currentBoardId && (
          <Button
            variant="outlined"
            onClick={onManageBoard}
            size="small"
            sx={{
              flex: { xs: 1, sm: 'none' },
              display: { xs: 'none', sm: 'inline-flex' }
            }}
          >
            Manage Board
          </Button>
        )}

        {/* Assignee Filter */}
        {currentBoardId && (
          <Autocomplete
            multiple
            disableClearable
            size="small"
            options={users}
            getOptionLabel={(option) => option.name}
            value={selectedAssignees}
            onChange={(_, newValue) => {
              onFiltersChange({
                ...filters,
                assigneeIds: newValue.length > 0 ? newValue.map((u) => u.user_id) : undefined,
              });
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.name} {...getTagProps({ index })} key={option.user_id} size="small" />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Assignees" />}
            sx={{ minWidth: 200, display: { xs: 'none', sm: 'flex' } }}
          />
        )}

        {/* Case Filter */}
        {currentBoardId && availableCases.length > 0 && (
          <Autocomplete
            multiple
            disableClearable
            size="small"
            options={availableCases}
            getOptionLabel={(option) => option.case_title}
            value={selectedCases}
            onChange={(_, newValue) => {
              onFiltersChange({
                ...filters,
                caseIds: newValue.length > 0 ? newValue.map((c) => c.case_id) : undefined,
              });
            }}
            isOptionEqualToValue={(option, value) => option.case_id === value.case_id}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                <Tooltip title={option.case_title.length > MAX_CASE_TITLE_LENGTH ? option.case_title : ''}>
                  <span>{truncateTitle(option.case_title)}</span>
                </Tooltip>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={truncateTitle(option.case_title)}
                  {...getTagProps({ index })}
                  key={option.case_id}
                  size="small"
                />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Cases" />}
            sx={{ minWidth: 200, display: { xs: 'none', sm: 'flex' } }}
          />
        )}

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Tooltip title="Clear all filters">
            <IconButton
              size="small"
              onClick={() => onFiltersChange({})}
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              <Clear fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* New Task Button */}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onNewTask}
          disabled={!currentBoardId}
          size="medium"
          sx={{
            ml: { xs: 0, sm: 'auto' },
            flex: { xs: 1, sm: 'none' },
            minWidth: { xs: 'auto', sm: 120 }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New Task</Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
        </Button>
      </Box>
      </Box>
    </Paper>
  );
};
